import { useNavigate, useParams } from "react-router-dom";
import generalRequest from "../../request/generalRequest";
import { useEffect, useState, useContext, useRef } from "react";
import "./shopPage.css";
import { CombinedContext } from "../../context/combinedContext";
import BookingModal from "../bookingModal/bookingModal";
import translations from "../../translations/translations";
import { Check, ChevronDown, Clock, Facebook, Flag, Globe, House, Instagram, MapPin, Monitor, Navigation, Phone, Sparkles, Star } from "lucide-react";
import ReportReviewModalCustomer from "../reportReviewModal/reportReviewModalCustomer";
import ShopSkeleton from "./shopSkeleton/shopSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";
import ShopGallery from "../common/shopGallery/shopGallery";
import CATEGORY_OPTIONS from "../../constants/categoryOptions";

/* Height of the sticky category bar — keep in sync with $sticky-offset in shopPage.css */
const STICKY_OFFSET = 78;

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

const formatTime24To12 = (time24) => {
	if (!time24) return "";
	const [h, m] = time24.split(":").map(Number);
	const period = h >= 12 ? "PM" : "AM";
	const hour12 = h % 12 || 12;
	return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
};

const toMinutes = (time24) => {
	if (!time24) return null;
	const [h, m] = time24.split(":").map(Number);
	if (Number.isNaN(h)) return null;
	return h * 60 + (m || 0);
};

const getCategoryLabel = (category, subCategory, language) => {
	const parent = CATEGORY_OPTIONS.find((c) => c.value === category);
	if (!parent) return null;
	const sub = parent.subCategories?.find((s) => s.value === subCategory);
	const target = sub && sub.value !== parent.value ? sub : parent;
	return language === "es" ? target.labelES : target.labelEN;
};

/* Live open/closed badge computed from the shop's own opening hours. */
const getOpenStatus = (openingHours, copy, language) => {
	if (!Array.isArray(openingHours) || openingHours.length < 7) return null;

	const now = new Date();
	const todayIndex = (now.getDay() + 6) % 7;
	const today = openingHours[todayIndex];
	if (!today) return null;

	const closedWith = (detail) => ({ isOpen: false, word: copy.closed, detail });

	/* When we reopen — "tomorrow 9:00 AM" reads better than a bare "Closed" */
	const nextOpening = () => {
		for (let step = 1; step <= 7; step += 1) {
			const day = openingHours[(todayIndex + step) % 7];
			if (!day || day.closed || !day.open) continue;
			const when = step === 1 ? copy.tomorrow : language === "es" ? day.dayES : day.dayEN;
			return `${copy.opensAt} ${when} ${formatTime24To12(day.open)}`;
		}
		return null;
	};

	if (today.closed) return closedWith(nextOpening());

	const open = toMinutes(today.open);
	const close = toMinutes(today.close);
	if (open === null || close === null) return null;

	const minutes = now.getHours() * 60 + now.getMinutes();
	const spansMidnight = close <= open;
	const isOpen = spansMidnight ? minutes >= open || minutes < close : minutes >= open && minutes < close;

	if (isOpen) return { isOpen: true, word: copy.open, detail: `${copy.until} ${formatTime24To12(today.close)}` };
	if (!spansMidnight && minutes < open) return closedWith(`${copy.opensAt} ${formatTime24To12(today.open)}`);
	return closedWith(nextOpening());
};

const formatPhone = (number) => {
	if (!number) return "";
	const digits = number.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return number.startsWith("+") ? number : `+${number}`;
};

const formatUrl = (url) => {
	if (!url) return "";
	return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
};

const TikTokIcon = ({ size = 18 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M16.6 3h-3.2v13.1a2.9 2.9 0 1 1-2.2-2.8v-3.3a6.2 6.2 0 1 0 5.4 6.1V9.4a8.6 8.6 0 0 0 4.4 1.4V7.6A5.5 5.5 0 0 1 16.6 3Z" />
	</svg>
);

const Stars = ({ value, size = 14 }) => {
	const rounded = Math.round(Number(value) || 0);
	return (
		<span className="sf-shop-stars" aria-label={`${value} / 5`}>
			{[1, 2, 3, 4, 5].map((i) => (
				<Star key={i} size={size} strokeWidth={1.5} fill={i <= rounded ? "currentColor" : "none"} className={i <= rounded ? "is-on" : "is-off"} />
			))}
		</span>
	);
};

const ShopPage = () => {
	const { shopId } = useParams();
	const { selectedService, setSelectedService, selectedstaff, language } = useContext(CombinedContext);

	const [shopData, setShopData] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [reportOpen, setReportOpen] = useState(false);
	const [reportReviewId, setReportReviewId] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);
	const [openCategories, setOpenCategories] = useState({});
	const [showFullWeek, setShowFullWeek] = useState(false);

	const categoryRefs = useRef({});
	const lockScrollSpy = useRef(0);
	const navigate = useNavigate();

	const t = translations[language].shopPage;

	useEffect(() => {
		async function loadShop() {
			try {
				const res = await generalRequest.getShopWithstaffsByIdPublic(shopId);
				setShopData(res.shop);
				setLoading(false);
			} catch (err) {
				console.error(err);
				setError(t.errorLoading);
				setLoading(false);
			}
		}
		loadShop();
	}, [shopId]);

	// A link shared before a rename still resolves, but the address bar should
	// settle on the shop's current one.
	useEffect(() => {
		if (!shopData?.slug || shopId.startsWith("shop_")) return;
		if (shopId === shopData.slug) return;

		navigate(`/shops/${shopData.slug}`, { replace: true });
	}, [shopData, shopId, navigate]);

	const servicesByCategory = (shopData?.serviceCategories || [])
		.map((cat) => ({
			category: cat,
			services: (shopData.services || []).filter((s) => s.categoryId === cat.categoryId),
		}))
		.filter((group) => group.services.length > 0);

	useEffect(() => {
		if (!servicesByCategory.length) return;
		setOpenCategories(Object.fromEntries(servicesByCategory.map(({ category }) => [category.categoryId, true])));
		setActiveCategory(servicesByCategory[0].category.categoryId);
	}, [shopData]);

	/* Keep the sticky tab bar in sync with whatever category is under it. */
	useEffect(() => {
		if (!servicesByCategory.length) return undefined;
		const ids = servicesByCategory.map(({ category }) => category.categoryId);

		const onScroll = () => {
			/* a tab click scrolls smoothly — don't let the spy fight it mid-flight */
			if (Date.now() < lockScrollSpy.current) return;
			let current = ids[0];
			ids.forEach((id) => {
				const el = categoryRefs.current[id];
				if (el && el.getBoundingClientRect().top <= STICKY_OFFSET + 12) current = id;
			});
			setActiveCategory((prev) => (prev === current ? prev : current));
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [shopData]);

	if (loading) return <ShopSkeleton />;
	if (error) return <p className="sf-shop-error">{error}</p>;

	const toggleCategory = (id) => setOpenCategories((prev) => ({ ...prev, [id]: prev[id] === false }));

	const jumpToCategory = (id) => {
		setActiveCategory(id);
		lockScrollSpy.current = Date.now() + 700;
		categoryRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const openBookingForService = (serviceId) => {
		setSelectedService(serviceId);
		setIsBookingOpen(true);
	};

	const handleReportReview = (reviewId) => {
		setReportReviewId(reviewId);
		setReportOpen(true);
	};

	const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);
	const reviews = Array.isArray(shopData.reviews) ? shopData.reviews : [];
	const reviewCount = reviews.length;
	const rating = (shopData.reviewSummary?.averageRating || (reviewCount > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount : 0)).toFixed(1);

	const categoryLabel = getCategoryLabel(shopData.category, shopData.subCategory, language);
	const status = getOpenStatus(shopData.openingHours, t.status, language);
	const serviceCount = shopData.services?.length || 0;
	const showCategoryChrome = servicesByCategory.length > 1;
	const todayIndex = (new Date().getDay() + 6) % 7;
	const todayHours = Array.isArray(shopData.openingHours) ? shopData.openingHours[todayIndex] : null;

	const socials = [
		{ key: "instagram", url: shopData.socialLinks?.instagram, Icon: Instagram, label: "Instagram" },
		{ key: "facebook", url: shopData.socialLinks?.facebook, Icon: Facebook, label: "Facebook" },
		{ key: "tiktok", url: shopData.socialLinks?.tiktok, Icon: TikTokIcon, label: "TikTok" },
		{ key: "website", url: shopData.socialLinks?.website, Icon: Globe, label: t.sidebar.websiteTitle || "Website" },
	].filter((s) => s.url);

	const serviceTag = (service) => {
		const loc = service.locationType || (service.isHomeService ? "home" : "in_shop");
		if (loc === "home") {
			return (
				<span className="sf-shop-service-tag is-home">
					<House size={12} strokeWidth={2} />
					{t.main.serviceType.home}
				</span>
			);
		}
		if (loc === "online") {
			return (
				<span className="sf-shop-service-tag is-online">
					<Monitor size={12} strokeWidth={2} />
					{t.main.serviceType.online}
				</span>
			);
		}
		return null;
	};

	return (
		<div className="sf-shop-container">
			<div className="sf-shop-content">
				<div className="sf-shop-main">
					<ShopGallery
						images={shopData.images || []}
						shareUrl={`https://reservasinfila.com/shops/${shopData.slug || shopData.shopId}`}
						onBack={() => navigate(-1)}
					/>

					{/* ── IDENTITY ── */}
					<header className="sf-shop-head">
						{categoryLabel && <span className="sf-shop-eyebrow">{categoryLabel}</span>}
						<h1 className="sf-shop-name">{shopData.name}</h1>

						<div className="sf-shop-meta">
							{reviewCount > 0 ? (
								<span className="sf-shop-rating-pill">
									<Star size={14} strokeWidth={1.5} fill="currentColor" />
									<strong>{rating}</strong>
									<span>({t.rating.reviews.replace("{{count}}", reviewCount)})</span>
								</span>
							) : (
								<span className="sf-shop-new-pill">
									<Sparkles size={13} strokeWidth={2} />
									{t.main.newShop}
								</span>
							)}

							{status && (
								<span className={`sf-shop-status ${status.isOpen ? "is-open" : "is-closed"}`}>
									<i className="sf-shop-status-dot" />
									<strong>{status.word}</strong>
									{status.detail && <span>· {status.detail}</span>}
								</span>
							)}
						</div>

						<p className="sf-shop-address">
							<MapPin size={15} strokeWidth={1.7} />
							<span>
								{shopData.address}
								{shopData.addressDescription && <em>{shopData.addressDescription}</em>}
							</span>
						</p>
					</header>

					{shopData.description && (
						<section className="sf-shop-about">
							<p>{shopData.description}</p>
						</section>
					)}

					{/* ── SERVICES ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.main.services}</h2>
							{serviceCount > 0 && <span className="sf-shop-count">{serviceCount}</span>}
						</div>

						{serviceCount > 0 ? (
							<div className="sf-shop-services">
								{/* a lone "Services" category is just the section title again — drop its chrome */}
								{showCategoryChrome && (
									<div className="sf-shop-cat-tabs">
										{servicesByCategory.map(({ category, services }) => (
											<button
												key={category.categoryId}
												type="button"
												className={`sf-shop-cat-tab ${activeCategory === category.categoryId ? "is-active" : ""}`}
												onClick={() => jumpToCategory(category.categoryId)}
											>
												{category.name?.[language]}
												<span>{services.length}</span>
											</button>
										))}
									</div>
								)}

								{servicesByCategory.map(({ category, services }) => {
									const isOpen = !showCategoryChrome || openCategories[category.categoryId] !== false;
									return (
										<div
											key={category.categoryId}
											ref={(el) => (categoryRefs.current[category.categoryId] = el)}
											className="sf-shop-cat-card"
										>
											{showCategoryChrome && (
												<button
													type="button"
													className="sf-shop-cat-header"
													onClick={() => toggleCategory(category.categoryId)}
													aria-expanded={isOpen}
												>
													<h3>{category.name?.[language]}</h3>
													<span className="sf-shop-cat-meta">
														{services.length}
														<ChevronDown size={16} className={`sf-shop-chevron ${isOpen ? "is-open" : ""}`} />
													</span>
												</button>
											)}

											<div className={`sf-shop-cat-body ${isOpen ? "is-open" : ""}`}>
												<div>
													{services.map((s) => (
														<div key={s.serviceId} className="sf-shop-service">
															<div className="sf-shop-service-copy">
																<h4 className="sf-shop-service-name">
																	{s.name}
																	{serviceTag(s)}
																</h4>
																{s.description && <p className="sf-shop-service-desc">{s.description}</p>}
																<span className="sf-shop-service-time">
																	<Clock size={13} strokeWidth={1.8} />
																	{s.duration} min
																</span>
															</div>

															<div className="sf-shop-service-action">
																<span className="sf-shop-service-price">{formatPrice(s.price, s.currency)}</span>
																<button type="button" className="sf-shop-book-btn" onClick={() => openBookingForService(s.serviceId)}>
																	{t.main.book}
																</button>
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<div className="sf-shop-empty">{t.main.noServices}</div>
						)}
					</section>

					{/* ── TEAM ── */}
					{shopData.staffs?.length > 0 && (
						<section className="sf-shop-section">
							<div className="sf-shop-section-head">
								<h2 className="sf-shop-h2">{t.sidebar.staff}</h2>
								<span className="sf-shop-count">{shopData.staffs.length}</span>
							</div>

							<div className="sf-shop-team">
								{shopData.staffs.map((b) => (
									<button
										type="button"
										className="sf-shop-team-card"
										key={b.staffId}
										onClick={() => navigate(`/shops/${shopData.slug || shopData.shopId}/staff/${b.staffId}`)}
									>
										{b.photo ? (
											<img className="sf-shop-team-avatar" src={b.photo} alt={b.firstName || ""} loading="lazy" />
										) : (
											<span className="sf-shop-team-avatar is-fallback">{b.firstName?.charAt(0).toUpperCase()}</span>
										)}
										<span className="sf-shop-team-name">{b.firstName}</span>
									</button>
								))}
							</div>
						</section>
					)}

					{/* ── ATTRIBUTES ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.main.attributes}</h2>
						</div>

						{shopData.attributes?.length > 0 ? (
							<div className="sf-shop-attrs">
								{shopData.attributes.map((attr) => (
									<span className="sf-shop-attr" key={attr.attributeId}>
										<span className="sf-shop-attr-icon">
											{attr.icon ? <img src={attr.icon} alt="" /> : <Check size={13} strokeWidth={2.6} />}
										</span>
										{language === "es" ? attr.nameES : attr.nameEN}
									</span>
								))}
							</div>
						) : (
							<div className="sf-shop-empty">{t.main.noAttributes}</div>
						)}
					</section>

					{/* ── REVIEWS ── */}
					<section className="sf-shop-section">
						<div className="sf-shop-section-head">
							<h2 className="sf-shop-h2">{t.main.reviews}</h2>
							{reviewCount > 0 && <span className="sf-shop-count">{reviewCount}</span>}
						</div>

						{reviewCount > 0 ? (
							<>
								<div className="sf-shop-rev-summary">
									<div className="sf-shop-rev-score">
										<span className="sf-shop-rev-score-num">{rating}</span>
										<Stars value={rating} size={16} />
										<span className="sf-shop-rev-score-count">{t.rating.reviews.replace("{{count}}", reviewCount)}</span>
									</div>

									<div className="sf-shop-rev-bars">
										{[5, 4, 3, 2, 1].map((star) => {
											const count = reviews.filter((r) => r.rating === star).length;
											const percent = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
											return (
												<div key={star} className="sf-shop-rev-bar-row">
													<span className="sf-shop-rev-bar-label">
														{star}
														<Star size={11} strokeWidth={0} fill="currentColor" />
													</span>
													<span className="sf-shop-rev-bar">
														<span className="sf-shop-rev-bar-fill" style={{ width: `${percent}%` }} />
													</span>
													<span className="sf-shop-rev-bar-count">{count}</span>
												</div>
											);
										})}
									</div>
								</div>

								<div className="sf-shop-rev-list">
									{reviews.map((rev) => (
										<article key={rev.reviewId} className="sf-shop-rev-card">
											<div className="sf-shop-rev-head">
												<div className="sf-shop-rev-who">
													<span className="sf-shop-rev-avatar">{(rev.customerName || "?").charAt(0).toUpperCase()}</span>
													<span>
														<span className="sf-shop-rev-name">{rev.customerName || t.main.anonymous}</span>
														<span className="sf-shop-rev-date">{new Date(rev.createdAt).toLocaleDateString(language === "es" ? "es-DO" : "en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
													</span>
												</div>

												<div className="sf-shop-rev-actions">
													<Stars value={rev.rating} size={13} />
													<button
														type="button"
														className="sf-shop-rev-flag"
														onClick={() => handleReportReview(rev.reviewId)}
														aria-label={t.main.reportReview}
														title={t.main.reportReview}
													>
														<Flag size={13} />
													</button>
												</div>
											</div>

											{rev.services?.length > 0 && (
												<div className="sf-shop-rev-chips">
													{rev.services.map((srv, i) => (
														<span key={i} className="sf-shop-rev-chip">
															{language === "es" ? srv.serviceNameEs : srv.serviceNameEn}
															<em>{srv.duration} min</em>
														</span>
													))}
												</div>
											)}

											{rev.comment && <p className="sf-shop-rev-text">{rev.comment}</p>}
										</article>
									))}
								</div>
							</>
						) : (
							<div className="sf-shop-empty">{t.main.noReviews}</div>
						)}
					</section>
				</div>

				{/* ── STICKY SIDEBAR ── */}
				<aside className="sf-shop-sidebar">
					<div className="sf-shop-card">
						{shopData.lat && shopData.lng && (
							<div className="sf-shop-map">
								<ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
							</div>
						)}

						<div className="sf-shop-card-body">
							<div className="sf-shop-place">
								<MapPin size={16} strokeWidth={1.7} />
								<span>
									<strong>{shopData.address}</strong>
									{(shopData.city || shopData.country) && <em>{[shopData.city, shopData.country].filter(Boolean).join(", ")}</em>}
								</span>
							</div>

							<a
								className="sf-shop-directions"
								href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Navigation size={15} strokeWidth={2} />
								{t.sidebar.getDirections}
							</a>

							<div className="sf-shop-rule" />

							<div className="sf-shop-block">
								<p className="sf-shop-label">{t.sidebar.contactAndHours}</p>

								<div className="sf-shop-contact">
									<a href={shopData.phone ? `tel:${shopData.phone}` : undefined} className="sf-shop-phone">
										<Phone size={15} strokeWidth={1.7} />
										{shopData.phone ? formatPhone(shopData.phone) : t.sidebar.noPhone}
									</a>
									{shopData.phone && (
										<a href={`tel:${shopData.phone}`} className="sf-shop-call">
											{t.sidebar.call}
										</a>
									)}
								</div>
							</div>

							{todayHours && (
								<div className="sf-shop-hours">
									<div className="sf-shop-hours-today">
										<span className="sf-shop-hours-day">
											<Clock size={14} strokeWidth={1.8} />
											{t.sidebar.hours.today}
										</span>
										<span className="sf-shop-hours-time">
											{todayHours.closed
												? t.sidebar.hours.closed
												: `${formatTime24To12(todayHours.open)} – ${formatTime24To12(todayHours.close)}`}
										</span>
									</div>

									<button type="button" className="sf-shop-hours-toggle" onClick={() => setShowFullWeek((v) => !v)}>
										{showFullWeek ? t.sidebar.hours.hideWeek : t.sidebar.hours.showWeek}
										<ChevronDown size={14} className={`sf-shop-chevron ${showFullWeek ? "is-open" : ""}`} />
									</button>

									<div className={`sf-shop-hours-week ${showFullWeek ? "is-open" : ""}`}>
										<div>
											{shopData.openingHours.map((dayObj, index) =>
												dayObj ? (
													<div key={index} className={`sf-shop-hours-row ${todayIndex === index ? "is-today" : ""}`}>
														<span>{language === "es" ? dayObj.dayES : dayObj.dayEN}</span>
														<span>
															{dayObj.closed
																? t.sidebar.hours.closed
																: `${formatTime24To12(dayObj.open)} – ${formatTime24To12(dayObj.close)}`}
														</span>
													</div>
												) : null,
											)}
										</div>
									</div>
								</div>
							)}

							{socials.length > 0 && (
								<>
									<div className="sf-shop-rule" />
									<div className="sf-shop-block">
										<p className="sf-shop-label">{t.sidebar.socialTitle}</p>
										<div className="sf-shop-socials">
											{socials.map(({ key, url, Icon, label }) => (
												<a key={key} href={formatUrl(url)} target="_blank" rel="noopener noreferrer" className="sf-shop-social" title={label}>
													<Icon size={18} strokeWidth={1.7} />
													<span>{label}</span>
												</a>
											))}
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</aside>
			</div>

			{isBookingOpen && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => setIsBookingOpen(false)}
					availabilityId={shopData.shopId}
					shopData={shopData}
					initialServiceId={selectedService}
					initialstaffId={selectedstaff}
				/>
			)}

			{reportOpen && <ReportReviewModalCustomer open={reportOpen} onClose={() => setReportOpen(false)} reviewId={reportReviewId} shopId={shopId} />}
		</div>
	);
};

export default ShopPage;
